import { Palette, PaletteOptions, Theme } from '@mui/material/styles';

import colors, { RGB } from 'color-name';

type ColorName = keyof typeof colors;
type CustomColors = { [key in ColorName]: RGB }

export const sharedVariants = [
  {
    props: { size: 'xs' as any },
    style: {
      fontSize: '0.8rem',
      padding: '4px 9px',
      minWidth: 'unset',
      '& .MuiButton-startIcon': {
        marginRight: '8px',
        marginLeft: 0,
      },
    },
  },
]

const customColorNames = [
  'brown',
  'chocolate',
  'darkblue',
  'darkcyan',
  'deeppink',
  'gold',
  //'orange', // this breaks using orange in ColoredTooltip, no time to debug now
  'salmon',
  'white',
  'ghostwhite',
  'whitesmoke',
  'yellow',
  'darkturquoise',
  'green',
  'darkorange',
  'orangered',
  'steelblue',
] as const;

type CustomColor = (typeof customColorNames)[number];

// new colors must be added to the default interfaces
// https://mui.com/material-ui/customization/palette/#custom-colors
type CustomPaletteOptions = {
  [key in CustomColor]: PaletteOptions['primary'];
};

type CustomPalette = {
  [key in CustomColor]: Palette['primary'];
};

type CustomComponentColorOverrides = {
  // importantly 'text' is defined in the theme by default, so not included in CustomColor 
  // adding it hear so buttons and the like can use the text color
  [key in CustomColor | 'text']: true;
};

declare module '@mui/material/styles' {
  interface PaletteOptions extends CustomPaletteOptions { }
  interface Palette extends CustomPalette { }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides extends CustomComponentColorOverrides { }
  interface ButtonPropsVariantOverrides { }
  interface ButtonPropsSizeOverrides {
    xs: true
  }
}

declare module '@mui/material/IconButton' {
  interface IconButtonPropsColorOverrides extends CustomComponentColorOverrides { }
}

declare module '@mui/material/Tabs' {
  interface TabsPropsIndicatorColorOverrides extends CustomComponentColorOverrides { }
}

declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides extends CustomComponentColorOverrides { }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsColorOverrides extends CustomComponentColorOverrides { }
}

declare module '@mui/material/TextField' {
  interface TextFieldPropsColorOverrides extends CustomComponentColorOverrides { }
}

declare module '@mui/material/Slider' {
  interface SliderPropsColorOverrides extends CustomComponentColorOverrides { }
}
// ToggleButtonGroup overrides
declare module '@mui/material/ToggleButton' {
  interface ToggleButtonPropsColorOverrides extends CustomComponentColorOverrides { }
}

declare module '@mui/material/ToggleButtonGroup' {
  interface ToggleButtonGroupPropsColorOverrides extends CustomComponentColorOverrides { }
}

const customColors = customColorNames.reduce((customColors, colorName) => {
  customColors[colorName] = colors[colorName];
  return customColors;
}, {} as CustomColors);

export const customColorsPallet = (baseTheme: Theme) => {
  return (
    (Object.keys(customColors) as CustomColor[])
      .reduce((acc, key) => {
        const colorVals = customColors[key];
        acc[key] = baseTheme.palette.augmentColor({
          color: {
            main: `rgb(${colorVals[0]}, ${colorVals[1]}, ${colorVals[2]})`,
          },
          name: key,
        });
        return acc;
      }, {} as CustomPaletteOptions)
  )
}